import { useCallback } from "react";

export const useRecorderManagement = (
  sessions,
  uploadSegment,
  callbacks = {},
  uploadQueue
) => {
  const { onFinalize, onReset } = callbacks || {};
  const { getSession, constants } = sessions;
  const { MIME, CHUNK_TIMESLICE_MS, OVERLAP_MS, MAX_SEGMENT_MS } = constants;

  const wireRecorder = useCallback(
    (mr, sessionId) => {
      const session = getSession(sessionId);
      if (!session) return;

      session.buffers.set(mr, { chunks: [], startedAt: Date.now() });
      session.recorders.add(mr);
      const flushInterval = setInterval(() => {
        if (mr.state === "recording") {
          try {
            mr.requestData(); // Force data availability
            console.log("🔄 Proactive flush at:", new Date().toISOString());
          } catch (e) {
            console.warn("Proactive flush failed:", e);
          }
        }
      }, 30000);
      mr.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        const buf = session.buffers.get(mr);
        if (!buf) return;
        buf.chunks.push(e.data);

        const elapsed = Date.now() - buf.startedAt;
        if (elapsed >= MAX_SEGMENT_MS && !session.rotating.has(mr)) {
          startNextThenStopCurrent(mr, sessionId);
        }
      };

      mr.onstop = async () => {
        clearInterval(flushInterval);
        const result = await handleRecorderStop(mr, sessionId, session);
        if (!result) return;
        const { isLastRecorder, isFinalSegment, segBlob } = result;

        // RESET PATH
        if (isLastRecorder && session.resetPending) {
          // Delegate to lifecycle reset if provided
          if (typeof onReset === "function") {
            onReset({ sessionId, session });
          }
          return;
        }

        // FINALIZE PATH
        if (isFinalSegment) {
          if (typeof onFinalize === "function") {
            onFinalize({ sessionId, session, segBlob });
          }
        }
      };
    },
    [getSession, MAX_SEGMENT_MS]
  );

  const startNextThenStopCurrent = useCallback(
    (oldMr, sessionId) => {
      const session = getSession(sessionId);
      if (!session) return;

      session.rotating.add(oldMr);
      const next = new MediaRecorder(session.stream, { mimeType: MIME });

      wireRecorder(next, sessionId);
      next.start(CHUNK_TIMESLICE_MS);

      setTimeout(() => {
        try {
          oldMr.stop();
        } catch {}
      }, OVERLAP_MS);
    },
    [getSession, MIME, CHUNK_TIMESLICE_MS, OVERLAP_MS, wireRecorder]
  );

  const handleRecorderStop = useCallback(
    async (mr, sessionId, session) => {
      await Promise.resolve();
      session.rotating.delete(mr);

      const buf = session.buffers.get(mr);
      session.buffers.delete(mr);
      session.recorders.delete(mr);

      const hasData = !!(buf && buf.chunks.length);
      const segBlob = hasData ? new Blob(buf.chunks, { type: MIME }) : null;

      const isLastRecorder = session.recorders.size === 0;
      const isFinalSegment =
        (isLastRecorder && !session.resetPending && !session.skipUpload) ||
        session.emergencyFlush;

      const segmentIndex = session.nextIndex ?? 0;
      session.nextIndex = segmentIndex + 1;
      const segmentId = `${sessionId}:${segmentIndex}`;

      console.log("💾 Persisting segment:", {
        segmentId,
        hasData,
        size: segBlob?.size,
        isFinalSegment,
        isLastRecorder,
        emergencyFlush: session.emergencyFlush,
      });

      if (segBlob && (!session.resetPending || session.emergencyFlush)) {
        if (uploadQueue) {
          await uploadQueue.enqueueNewSegment(
            sessionId,
            {
              id: segmentId,
              index: segmentIndex,
              isFinal: isFinalSegment, // ✅ This tells the queue if it's the final segment
            },
            segBlob
          );
        }
        if (session.emergencyFlush && isFinalSegment) {
          session.emergencyProcessed = true;
        }
      }

      return { isLastRecorder, isFinalSegment, segBlob };
    },
    [MIME, uploadQueue]
  );

  const createRecorder = useCallback(
    (stream, sessionId) => {
      const mr = new MediaRecorder(stream, { mimeType: MIME });
      wireRecorder(mr, sessionId);
      mr.start(CHUNK_TIMESLICE_MS);
      return mr;
    },
    [MIME, CHUNK_TIMESLICE_MS, wireRecorder]
  );

  const stopAllRecorders = useCallback(
    (sessionId) => {
      const session = getSession(sessionId);
      if (!session) return;

      for (const mr of Array.from(session.recorders)) {
        try {
          try {
            if (mr.state === "recording" || mr.state === "paused")
              mr.requestData();
          } catch {}
          try {
            mr.stop();
          } catch {}
        } catch {}
      }
    },
    [getSession]
  );

  const pauseLatestRecorder = useCallback(
    (sessionId) => {
      const session = getSession(sessionId);
      if (!session) return null;

      const mr = Array.from(session.recorders).pop();
      if (mr && mr.state === "recording") {
        mr.pause();
        return mr;
      }
      return null;
    },
    [getSession]
  );

  const resumeLatestRecorder = useCallback(
    (sessionId) => {
      const session = getSession(sessionId);
      if (!session) return null;

      const mr = Array.from(session.recorders).pop();
      if (mr && mr.state === "paused") {
        mr.resume();
        return mr;
      }
      return null;
    },
    [getSession]
  );

  return {
    wireRecorder,
    createRecorder,
    stopAllRecorders,
    pauseLatestRecorder,
    resumeLatestRecorder,
    handleRecorderStop,
  };
};
